<script setup lang="ts">
import { computed, reactive, ref } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import type { AdminRecord } from '@/types/record'
import type { FormFieldDef } from '@/types/page'
import { pageById } from '@/data/registry'
import { usePageTable } from '@/composables/usePageTable'
import { useAuthStore } from '@/stores/auth'
import { usePrototypeStore } from '@/stores/prototype'
import { adapter } from '@/services/prototype-adapter'
import { request } from '@/services/request'
import { ApiError } from '@/utils/errors'
import { formatDateTime, formatNumber, nowIso } from '@/utils/format'
import { ASSET_FOLDERS } from '@/data/seed'
import AppIcon from '@/components/AppIcon.vue'
import ProSummary from '@/components/pro/ProSummary.vue'
import ProFilters from '@/components/pro/ProFilters.vue'
import ProPagination from '@/components/pro/ProPagination.vue'
import AppDialog from '@/components/ui/AppDialog.vue'
import ProField from '@/components/pro/ProField.vue'

const definition = pageById('assets')!
if (!definition) throw new Error('附件库页面定义缺失')


const auth = useAuthStore()
const store = usePrototypeStore()
const table = usePageTable(() => definition)

const previewOpen = ref(false)
const previewAsset = ref<AdminRecord | null>(null)
const uploadOpen = ref(false)
const uploadInput = ref<HTMLInputElement | null>(null)
const uploadMode = ref<'create' | 'replace'>('create')
const uploadTarget = ref<AdminRecord | null>(null)
const uploadFileName = ref('')
const uploadProgress = ref(0)
const uploadBusy = ref(false)
const uploadError = ref('')
const uploadRetryable = ref(false)
const selected = ref<string[]>([])
const form = reactive({
  name: '',
  folder: '内容封面',
  asset_type: '图片',
  alt_text: '',
})
const errors = reactive<Record<string, string>>({})

const canUpload = computed(() => auth.has('assets.upload'))
const canReplace = computed(() => auth.has('assets.replace'))
const canDelete = computed(() => auth.has('assets.delete'))
const allSelected = computed(() => table.rows.value.length > 0 && selected.value.length === table.rows.value.length)

const uploadFields: FormFieldDef[] = [
  { key: 'name', label: '素材名称', type: 'text', required: true, maxLength: 60 },
  { key: 'folder', label: '所属目录', type: 'select', options: ASSET_FOLDERS.map(value => ({ value, label: value })), required: true },
  { key: 'asset_type', label: '素材类型', type: 'select', options: ['图片', '文档', '视频'].map(value => ({ value, label: value })), required: true },
  { key: 'alt_text', label: '替代文本', type: 'text', full: true, maxLength: 120 },
]

function tone(asset: AdminRecord | null | undefined): string {
  return String(asset?.cover_color ?? '#ddf3ea')
}

function assetGlyph(asset: AdminRecord): string {
  const name = String(asset?.name ?? '')
  const chinese = name.match(/[\u4e00-\u9fa5]/)
  if (chinese?.[0]) return chinese[0]
  const type = String(asset?.asset_type ?? '素材')
  return type === '图片' ? '图' : type === '视频' ? '视' : '文'
}

function fileLabel(asset: AdminRecord): string {
  return asset.asset_type === '图片' ? '图片素材' : asset.asset_type === '视频' ? '视频素材' : '文档素材'
}

function openPreview(asset: AdminRecord): void {
  previewAsset.value = asset
  previewOpen.value = true
}

function openUpload(): void {
  uploadMode.value = 'create'
  uploadTarget.value = null
  uploadFileName.value = ''
  uploadProgress.value = 0
  uploadError.value = ''
  uploadRetryable.value = false
  form.name = ''
  form.folder = '内容封面'
  form.asset_type = '图片'
  form.alt_text = ''
  for (const key of Object.keys(errors)) delete errors[key]
  uploadOpen.value = true
}

function openReplace(asset: AdminRecord): void {
  if (!canReplace.value) return
  uploadMode.value = 'replace'
  uploadTarget.value = asset
  uploadFileName.value = ''
  uploadProgress.value = 0
  uploadError.value = ''
  uploadRetryable.value = false
  form.name = String(asset.name ?? '')
  form.folder = String(asset.folder ?? '内容封面')
  form.asset_type = String(asset.asset_type ?? '图片')
  form.alt_text = String(asset.alt_text ?? '')
  for (const key of Object.keys(errors)) delete errors[key]
  uploadOpen.value = true
}

function pickUploadFile(event: Event): void {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  if (!file) return
  uploadFileName.value = file.name
  if (!form.name) form.name = file.name.replace(/\.[^.]+$/, '')
  target.value = ''
}

function chooseUploadFile(): void {
  uploadInput.value?.click()
}

function validate(): boolean {
  for (const key of Object.keys(errors)) delete errors[key]
  if (!uploadFileName.value) errors.file = '请选择要上传的文件'
  if (!form.name.trim()) errors.name = '请填写素材名称'
  if (!form.folder) errors.folder = '请选择所属目录'
  if (!form.asset_type) errors.asset_type = '请选择素材类型'
  if (form.asset_type === '图片' && uploadFileName.value && !/\.(png|jpe?g|webp)$/i.test(uploadFileName.value)) {
    errors.file = '图片素材仅支持 PNG、JPG、WEBP'
  }
  return Object.keys(errors).length === 0
}

function estimateSize(): number {
  return Math.max(12, Math.round((uploadFileName.value.length * 37 + form.name.length * 19) % 4200))
}

async function upload(): Promise<void> {
  if (uploadBusy.value) return
  if (!validate()) {
    uploadError.value = '请检查文件类型与必填的素材信息后再提交'
    return
  }
  uploadBusy.value = true
  uploadProgress.value = 8
  uploadError.value = ''
  uploadRetryable.value = false
  const timer = window.setInterval(() => {
    uploadProgress.value = Math.min(92, uploadProgress.value + 9)
  }, 120)
  try {
    await request(
      () => {
        const actor = auth.profile?.name ?? '管理员'
        if (uploadMode.value === 'replace' && uploadTarget.value) {
          const result = store.updateRecord(definition, uploadTarget.value.id, {
            ...uploadTarget.value,
            file_name: uploadFileName.value,
            alt_text: form.alt_text,
            name: form.name,
            folder: form.folder,
            asset_type: form.asset_type,
            size_kb: estimateSize(),
            replaced_at: nowIso(),
            version: Number(uploadTarget.value.version ?? 1) + 1,
          })
          if (!result.ok) throw new Error(result.message)
          return result.message
        }
        adapter.addAsset({
          name: form.name.trim(),
          folder: form.folder,
          type: form.asset_type,
          sizeKb: estimateSize(),
          broken: form.name.includes('损坏'),
        }, actor)
        return '素材已上传到当前原型内存'
      },
      { write: true, delay: 300, useSimulation: true },
    )
    uploadProgress.value = 100
    uploadBusy.value = false
    uploadOpen.value = false
    table.afterMutation()
    ElMessage.success(uploadMode.value === 'replace' ? '素材文件已替换' : '素材已上传到当前原型内存')
  } catch (error) {
    uploadBusy.value = false
    uploadError.value = error instanceof Error ? error.message : '上传失败，请重试'
    uploadRetryable.value = true
    uploadProgress.value = 0
  } finally {
    window.clearInterval(timer)
  }
}

function toggleAll(): void {
  selected.value = allSelected.value ? [] : table.rows.value.map(row => String(row.id))
}

function toggle(id: string): void {
  selected.value = selected.value.includes(id) ? selected.value.filter(item => item !== id) : [...selected.value, id]
}

async function archiveOne(asset: AdminRecord): Promise<void> {
  if (!canDelete.value) return
  const ok = await confirmArchive(1)
  if (!ok) return
  try {
    const result = adapter.executeRowAction(definition, String(asset.id), 'archive', {}, auth.profile?.name ?? '管理员')
    table.afterMutation()
    table.selectedKeys.value = []
    ElMessage.success(result.message)
  } catch (error) {
    const message = error instanceof ApiError ? error.message : '归档失败'
    ElMessage.error(message)
  }
}

async function archiveSelected(): Promise<void> {
  if (!selected.value.length || !canDelete.value) return
  const ok = await confirmArchive(selected.value.length)
  if (!ok) return
  const failures: string[] = []
  for (const id of selected.value) {
    try {
      adapter.executeRowAction(definition, id, 'archive', {}, auth.profile?.name ?? '管理员')
    } catch (error) {
      failures.push(error instanceof ApiError ? error.message : '归档失败')
    }
  }
  selected.value = []
  table.afterMutation()
  if (failures.length) ElMessage.error(`${failures.length} 个素材仍被引用，未归档：${failures[0]}`)
}

async function deleteAsset(asset: AdminRecord): Promise<void> {
  if (!canDelete.value) return
  try {
    await ElMessageBox.confirm(
      '删除后该素材会从当前原型内存移除，已归档素材仍会先检查引用关系。',
      '确认删除素材',
      { type: 'warning', confirmButtonText: '删除', cancelButtonText: '取消' },
    )
  } catch {
    return
  }
  try {
    await request(
      () => adapter.remove(definition, String(asset.id), auth.profile?.name ?? '管理员'),
      { write: true, delay: 280, useSimulation: true },
    )
    selected.value = selected.value.filter(id => id !== String(asset.id))
    table.afterMutation()
    ElMessage.success('素材已删除')
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '删除失败')
  }
}

async function confirmArchive(count: number): Promise<boolean> {
  try {
    await ElMessageBox.confirm(
      count > 1 ? `确认归档选中的 ${count} 个素材？被引用的素材会提示冲突。` : '确认归档该素材？被引用时会提示冲突。',
      '确认归档素材',
      { type: 'warning', confirmButtonText: '归档', cancelButtonText: '取消' },
    )
    return true
  } catch {
    return false
  }
}

function fallback(asset: AdminRecord): void {
  asset.preview_error = true
}
</script>

<template>
  <section class="table-page">
    <header class="page-header">
      <div class="page-header__text">
        <div class="page-header__title-row">
          <h1 class="page-title">附件库</h1>
          <span class="prototype-pill">原型数据</span>
        </div>
        <p class="page-subtitle">{{ definition.purpose }}</p>
      </div>
      <div class="page-header__actions">
        <button v-if="canUpload" type="button" class="btn btn--primary" @click="openUpload">
          <AppIcon name="upload" :size="15" />上传素材
        </button>
        <button v-if="selected.length && canDelete" type="button" class="btn btn--danger" @click="archiveSelected">
          <AppIcon name="archive" :size="14" />归档选中（{{ selected.length }}）
        </button>
      </div>
    </header>

    <ProSummary :definition="definition" :summary="table.summary" />

    <ProFilters
      :model-value="table.filters"
      :definition="definition"
      :keyword="table.keyword.value"
      :active-count="table.activeFilterCount.value"
      @update:model-value="value => { Object.assign(table.filters, value) }"
      @update:keyword="table.keyword.value = $event"
      @reset="table.reset"
      @search="table.reload"
    />

    <div class="table-card">
      <div class="table-card__head">
        <div class="table-card__title">素材列表 <span class="table-card__count">（{{ formatNumber(table.total.value) }} 个）</span></div>
        <div class="table-card__tools">
          <label v-if="canDelete && table.rows.value.length" class="checkbox-line">
            <el-checkbox :model-value="allSelected" aria-label="全选当前页素材" @change="toggleAll" />
            <span>全选</span>
          </label>
          <button type="button" class="table-tool" aria-label="刷新素材" title="刷新素材" @click="table.refresh"><AppIcon name="refresh" :size="15" /></button>
        </div>
      </div>

      <div v-if="table.loading.value" class="table-skeleton" aria-label="素材加载中" aria-busy="true">
        <div v-for="index in 6" :key="index" class="table-skeleton__row">
          <span class="sk sk--avatar" /><span class="sk sk--wide" /><span class="sk" /><span class="sk sk--short" />
        </div>
      </div>

      <div v-else-if="table.status.value === 'forbidden'" class="empty-state" role="alert">
        <span class="empty-state__art"><AppIcon name="lock" :size="44" /></span>
        <h3 class="empty-state__title">当前角色没有查看附件库的权限</h3>
        <p class="empty-state__text">需要权限 assets.view。可返回工作台或由超级管理员调整角色授权。</p>
        <div class="empty-state__actions">
          <button type="button" class="btn btn--primary" @click="$router.push({ path: '/403', query: { from: $route.fullPath, need: 'assets.view' } })">查看权限说明</button>
          <button type="button" class="btn" @click="$router.push('/dashboard')">返回工作台</button>
        </div>
      </div>

      <div v-else-if="table.status.value === 'error' || table.status.value === 'offline'" class="empty-state" role="alert">
        <span class="empty-state__art"><AppIcon :name="table.status.value === 'offline' ? 'wifi-off' : 'warning'" :size="44" /></span>
        <h3 class="empty-state__title">{{ table.status.value === 'offline' ? '网络连接已断开' : '素材加载失败' }}</h3>
        <p class="empty-state__text">{{ table.error.value }}</p>
        <div class="empty-state__actions">
          <button type="button" class="btn btn--primary" @click="table.refresh">重试</button>
          <button type="button" class="btn" @click="table.reset">清空筛选</button>
        </div>
      </div>

      <div v-else-if="table.status.value === 'empty'" class="empty-state">
        <span class="empty-state__art"><AppIcon name="image" :size="44" /></span>
        <h3 class="empty-state__title">没有符合筛选条件的素材</h3>
        <p class="empty-state__text">可清空筛选或上传新的图片、文档与视频。</p>
        <div class="empty-state__actions">
          <button type="button" class="btn" @click="table.reset">清空筛选</button>
          <button v-if="canUpload" type="button" class="btn btn--primary" @click="openUpload">上传素材</button>
        </div>
      </div>

      <div v-else class="grid-page" :class="{ 'is-refreshing': table.refreshing.value }">
        <article v-for="asset in table.rows.value" :key="String(asset.id)" class="asset-card">
          <button type="button" class="asset-card__preview" :aria-label="`预览 ${asset.name}`" @click="openPreview(asset)">
            <img
              v-if="asset.status !== 'archived'"
              :src="`data:image/svg+xml;charset=utf-8,${encodeURIComponent(`<svg xmlns='http://www.w3.org/2000/svg' width='640' height='400'><rect width='640' height='400' fill='${tone(asset)}'/><circle cx='540' cy='70' r='120' fill='rgba(255,255,255,.28)'/><text x='32' y='220' font-size='42' font-family='PingFang SC, sans-serif' fill='#2f5c4c'>${assetGlyph(asset)}</text><text x='32' y='272' font-size='21' font-family='PingFang SC, sans-serif' fill='#4b7a68'>${fileLabel(asset)}</text></svg>`)}`"
              :alt="String(asset.alt_text || asset.name)"
              loading="lazy"
              decoding="async"
              @error="fallback(asset)"
            />
            <span v-if="asset.preview_error" class="asset-card__placeholder" :style="{ '--asset-tone': tone(asset) }">
              <AppIcon :name="asset.asset_type === '视频' ? 'video' : asset.asset_type === '文档' ? 'file' : 'image'" :size="26" />
              <b>{{ assetGlyph(asset) }}</b>
              <span>预览暂不可用 · 已使用固定占位</span>
            </span>
            <span class="asset-card__type">{{ asset.asset_type || '素材' }}</span>
          </button>
          <div class="asset-card__body">
            <div class="asset-card__title">
              <el-checkbox
                v-if="canDelete"
                :model-value="selected.includes(String(asset.id))"
                :aria-label="`选择 ${asset.name}`"
                @change="toggle(String(asset.id))"
              />
              <b>{{ asset.name }}</b>
              <span class="tag" :class="asset.status === 'archived' ? 'tag--neutral' : 'tag--success'">{{ asset.status === 'archived' ? '已归档' : '有效' }}</span>
            </div>
            <div class="asset-card__meta">
              <span>{{ asset.folder }}</span>
              <span>·</span>
              <span>{{ formatNumber(asset.size_kb) }} KB</span>
              <span>·</span>
              <span>引用 {{ asset.usage_count ?? 0 }}</span>
            </div>
            <div class="asset-card__meta">{{ formatDateTime(asset.uploaded_at) }}</div>
            <div class="asset-card__actions">
              <button type="button" class="row-action" @click="openPreview(asset)">预览</button>
              <button v-if="canReplace" type="button" class="row-action" @click="openReplace(asset)">替换</button>
              <button v-if="canDelete && asset.status !== 'archived'" type="button" class="row-action row-action--danger" @click="archiveOne(asset)">归档</button>
              <button v-if="canDelete && asset.status === 'archived'" type="button" class="row-action row-action--danger" @click="deleteAsset(asset)">删除</button>
            </div>
          </div>
        </article>
      </div>

      <ProPagination
        v-if="table.status.value !== 'loading' && table.status.value !== 'error' && table.status.value !== 'forbidden' && table.status.value !== 'empty'"
        :page="table.page.value"
        :page-size="table.pageSize.value"
        :total="table.total.value"
        @update:page="table.setPage"
        @update:page-size="table.setPageSize"
      />
    </div>

    <AppDialog v-model="previewOpen" :title="String(previewAsset?.name ?? '素材预览')" width="640px">
      <div class="preview-dialog__media">
        <img
          v-if="previewAsset && !previewAsset.preview_error"
          :src="`data:image/svg+xml;charset=utf-8,${encodeURIComponent(`<svg xmlns='http://www.w3.org/2000/svg' width='960' height='600'><rect width='960' height='600' fill='${tone(previewAsset)}'/><text x='46' y='290' font-size='82' font-family='PingFang SC, sans-serif' fill='#2f5c4c'>${assetGlyph(previewAsset)}</text><text x='46' y='356' font-size='28' font-family='PingFang SC, sans-serif' fill='#4b7a68'>${previewAsset.name}</text></svg>`)}`"
          :alt="String(previewAsset.alt_text || previewAsset.name)"
          @error="previewAsset.preview_error = true"
        />
        <div v-else class="asset-card__placeholder" :style="{ '--asset-tone': tone(previewAsset ?? {}) }">
          <AppIcon name="image" :size="38" />
          <b>{{ previewAsset ? assetGlyph(previewAsset) : '素' }}</b>
          <span>原文件不可用，已回退到固定占位</span>
        </div>
      </div>
      <div class="detail-grid" style="margin-top: 12px">
        <div class="detail-item"><span class="detail-item__label">素材类型</span><div class="detail-item__value">{{ previewAsset?.asset_type }}</div></div>
        <div class="detail-item"><span class="detail-item__label">所属目录</span><div class="detail-item__value">{{ previewAsset?.folder }}</div></div>
        <div class="detail-item"><span class="detail-item__label">文件大小</span><div class="detail-item__value">{{ formatNumber(previewAsset?.size_kb) }} KB</div></div>
        <div class="detail-item"><span class="detail-item__label">引用位置</span><div class="detail-item__value">{{ previewAsset?.references || '尚未被引用' }}</div></div>
        <div class="detail-item detail-item--full"><span class="detail-item__label">上传时间</span><div class="detail-item__value">{{ formatDateTime(previewAsset?.uploaded_at) }}</div></div>
      </div>
      <template #footer>
        <button type="button" class="btn" @click="previewOpen = false">关闭</button>
        <button v-if="canReplace && previewAsset" type="button" class="btn btn--primary" @click="openReplace(previewAsset)">替换文件</button>
      </template>
    </AppDialog>

    <AppDialog v-model="uploadOpen" :title="uploadMode === 'replace' ? '替换素材文件' : '上传素材'" width="520px">
      <div class="upload-drop" @click="chooseUploadFile">
        <AppIcon name="upload" :size="26" />
        <b>{{ uploadFileName || (uploadMode === 'replace' ? '点击选择替换文件' : '点击选择文件') }}</b>
        <small>支持 PNG、JPG、WEBP、PDF、MP4，文件只保留在当前原型内存</small>
        <input ref="uploadInput" type="file" hidden accept=".png,.jpg,.jpeg,.webp,.pdf,.mp4" @change="pickUploadFile" />
      </div>
      <p v-if="errors.file" class="pro-field__error" role="alert">{{ errors.file }}</p>

      <div class="settings-form-grid" style="margin-top: 12px">
        <ProField v-model="form.name" :field="uploadFields[0]!" :error="errors.name" />
        <ProField v-model="form.folder" :field="uploadFields[1]!" :options="uploadFields[1]!.options" :error="errors.folder" />
        <ProField v-model="form.asset_type" :field="uploadFields[2]!" :options="uploadFields[2]!.options" :error="errors.asset_type" />
        <ProField v-model="form.alt_text" :field="uploadFields[3]!" :error="errors.alt_text" />
      </div>

      <div v-if="uploadBusy || uploadProgress" style="margin-top: 12px">
        <div class="upload-progress"><i :style="{ width: `${uploadProgress}%` }" /></div>
        <div class="muted" style="margin-top: 4px">{{ uploadBusy ? `正在上传… ${uploadProgress}%` : '上传完成' }}</div>
      </div>
      <div v-if="uploadError" class="info-banner info-banner--danger" style="margin-top: 10px" role="alert">
        <AppIcon name="warning" :size="14" />
        <span>{{ uploadError }}</span>
      </div>
      <template #footer>
        <button type="button" class="btn" :disabled="uploadBusy" @click="uploadOpen = false">取消</button>
        <button v-if="uploadRetryable" type="button" class="btn" :disabled="uploadBusy" @click="upload">重试上传</button>
        <button type="button" class="btn btn--primary" :disabled="uploadBusy" @click="upload">
          {{ uploadBusy ? '提交中…' : uploadMode === 'replace' ? '确认替换' : '确认上传' }}
        </button>
      </template>
    </AppDialog>
  </section>
</template>

<style scoped>
.table-skeleton__row { display: flex; align-items: center; gap: 14px; height: var(--row-h); border-bottom: 1px solid var(--c-divider); }
.sk { height: 9px; border-radius: 5px; background: linear-gradient(90deg, #f2f5f8 20%, #e9eff4 40%, #f2f5f8 60%); background-size: 200% 100%; animation: shimmer 1.25s linear infinite; width: 64px; }
.sk--avatar { width: 28px; height: 28px; border-radius: 50%; }
.sk--wide { width: 180px; }
.sk--short { width: 80px; }
@keyframes shimmer { to { background-position: -200% 0; } }
.is-refreshing { opacity: .64; pointer-events: none; }
</style>
